import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {resolve} from '../../../api/endPoints';
import axiosClient from '../../../utils/axios';
import { updatePostApproval } from '../lomavisCreator/lomavisCreator';
import {readLomavisCreatorConfig} from '../lomavisCreator/lomavisCreatorConfig';
import {
  APPROVAL_STEP_MODE_GROUP,
  APPROVAL_STEP_MODE_USER,
  APPROVAL_USERS_RULE_ANY,
  CONFIG_SELECT_OPTIONS,
  RIGHT_COLUMN_OPTIONS,
  STATUS_ACTIVE,
  STATUS_DRAFT,
  STEP_MODE_SINGLE_STEP,
  TableSelectOption,
  TARGET_PUBLISH_WITH_SCHEDULE
} from './constants';
import {fetchAllPaginatedResults} from "../../../utils/http/fetchAllPaginatedResults";

type TargetType = 'publish' | 'save_as_approved_post';
type SelectType = TableSelectOption.USER | TableSelectOption.GROUP;
type CurrentStepType = Number;
const creator_config = readLomavisCreatorConfig();
interface ApprovalDefaultSettingsData {
  uuid?: string;
  location?: string;
  default_approval_user: any | null;
  default_approval_group: any | null;
}

interface LomavisPostApprovalState {
  loading: boolean;
  error: string | null;
  ui: Record<string, any>;
  default_settings: {
    loading: boolean;
    error: any;
    data: ApprovalDefaultSettingsData | null;
    local_data: ApprovalDefaultSettingsData;
    applied: boolean;
  };
  featuresEnabled: {
    approvalProcessMultistep: boolean,
    approvalProcessSaveAsApprovedPost: boolean,
    approvalProcessGroups: boolean,
  },
  local_states: {
    selected_config_tab: String;
    delete_group_data: {
      uuid: string;
      name: string;
      members: any[];
      approval_step_users_rule: number;
      has_been_saved: boolean;
    };
    local_changes: boolean;
    right_column_options: String;
  };
  data: {
    approval_process_final_decision_timestamp?: boolean|null;
    last_notification_sent_at?: string|null;
    cloud_uuid?: string;
    location_uuid?: string;
    is_multipost?: boolean;
    step_mode: Number;
    target: Number;
    status: Number;
    private_message: any;
    public_message: any;
    select_type: SelectType;
    current_step: CurrentStepType;
    create_group_data: {
      uuid: string;
      name: string;
      members: any[];
      approval_step_users_rule: number;
      has_been_saved: boolean;
    };
    edit_group_data: {
      uuid: string;
      name: string;
      members: any[];
      approval_step_users_rule: number;
      has_been_saved: boolean;
    };
    users: any[];
    groups: any[];

    approval_step_1_users_rule: number;
    approval_step_2_users_rule: number;
    approval_step_1_users: any[];
    approval_step_2_users: any[];
    approval_step_2_group: any[];
    approval_step_1_group: any[];
  };
}

export const LomavisPostApprovalStateInitialState: LomavisPostApprovalState = {
  loading: false,
  error: null,
  featuresEnabled: {
    approvalProcessMultistep: false,
    approvalProcessSaveAsApprovedPost: false,
    approvalProcessGroups: false,
  },
  ui: {
    showPostApprovalCreatorModal: false,
    showCreateGroupModal: false,
    showEditGroupModal: false,
    showDeleteConfirmationModal: false,
    showDefaultSettingsModal: false
  },
  default_settings: {
    loading: false,
    error: null,
    data: null,
    local_data: {
      default_approval_user: null,
      default_approval_group: null
    },
    applied: false
  },
  local_states: {
    selected_config_tab: CONFIG_SELECT_OPTIONS.CONFIG,
    delete_group_data: {
      uuid: '',
      name: '',
      members: [],
      approval_step_users_rule: 1,
      has_been_saved: false
    },
    local_changes: false,
    right_column_options: RIGHT_COLUMN_OPTIONS.RIGHT_COLUMN_USERS
  },

  data: {
    cloud_uuid: creator_config?.cloud?.uuid,
    location_uuid: creator_config?.location?.uuid,
    is_multipost: creator_config?.is_multipost,
    last_notification_sent_at: null,
    step_mode: STEP_MODE_SINGLE_STEP,
    target: TARGET_PUBLISH_WITH_SCHEDULE,
    status: STATUS_DRAFT,
    private_message: { ops: [{ insert: '\n' }] },
    public_message: { ops: [{ insert: '\n' }] },
    select_type: TableSelectOption.USER,
    current_step: 1,
    edit_group_data: {
      uuid: '',
      name: '',
      members: [],
      approval_step_users_rule: undefined,
      has_been_saved: false
    },
    create_group_data: {
      uuid: '',
      name: '',
      members: [],
      approval_step_users_rule: APPROVAL_USERS_RULE_ANY,
      has_been_saved: false
    },
    users: [],
    groups: [],

    approval_step_1_users_rule: APPROVAL_USERS_RULE_ANY,
    approval_step_2_users_rule: APPROVAL_USERS_RULE_ANY,
    approval_step_1_users: [],
    approval_step_2_users: [],
    approval_step_2_group: null,
    approval_step_1_group: null
  }
};

const normalizeApprovalUserStatus = (approvalUser: any) => {
  if (!approvalUser?.user || typeof approvalUser.user !== 'object') {
    return approvalUser;
  }

  return {
    ...approvalUser.user,
    is_approved: approvalUser.is_approved,
    is_approved_timestamp: approvalUser.is_approved_timestamp
  };
};

const normalizeApprovalDataPayload = (payload: any) => {
  if (!payload) {
    return payload;
  }

  const normalizedPayload = { ...payload };
  if (Array.isArray(payload.approval_step_1_users)) {
    normalizedPayload.approval_step_1_users =
      payload.approval_step_1_users.map(normalizeApprovalUserStatus);
  }
  if (Array.isArray(payload.approval_step_2_users)) {
    normalizedPayload.approval_step_2_users =
      payload.approval_step_2_users.map(normalizeApprovalUserStatus);
  }

  return normalizedPayload;
};

const lomavisPostApprovalSlice = createSlice({
  name: 'lomavisPostApproval',
  initialState: LomavisPostApprovalStateInitialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setApprovalUI(state, action: PayloadAction<{ key: string; value: any }>) {
      state.ui[action.payload.key] = action.payload.value;
    },
    setDefaultSettingsLoading(state, action: PayloadAction<boolean>) {
      state.default_settings.loading = action.payload;
    },
    setDefaultSettingsError(state, action: PayloadAction<any>) {
      state.default_settings.error = action.payload;
    },
    loadDefaultApprovalSettings(
      state,
      action: PayloadAction<ApprovalDefaultSettingsData>
    ) {
      state.default_settings.data = action.payload;
      state.default_settings.local_data = {
        uuid: action.payload.uuid,
        location: action.payload.location,
        default_approval_user: action.payload.default_approval_user || null,
        default_approval_group: action.payload.default_approval_group || null
      };
      state.default_settings.error = null;
    },
    setDefaultApprovalSettingsValue(
      state,
      action: PayloadAction<{ key: string; value: any }>
    ) {
      state.default_settings.local_data[action.payload.key] =
        action.payload.value;
    },
    markDefaultApprovalSettingsApplied(state, action: PayloadAction<boolean>) {
      state.default_settings.applied = action.payload;
    },
    setApprovalConfiguration(
      state,
      action: PayloadAction<{ key: string; value: any }>
    ) {
      state.data[action.payload.key] = action.payload.value;
      state.local_states.local_changes = true;
    },
    setApprovalKey(state, action: PayloadAction<{ key: string; value: any }>) {
      state.data[action.payload.key] = action.payload.value;
      state.local_states.local_changes = true;
    },
    setGroupValue(state, action: PayloadAction<{ key: string; value: any }>) {
      state.data.create_group_data[action.payload.key] = action.payload.value;
    },
    setEditGroupValue(
      state,
      action: PayloadAction<{ key: string; value: any }>
    ) {
      state.data.edit_group_data[action.payload.key] = action.payload.value;
    },
    setGroupForEdit(state, action: PayloadAction<any>) {
      state.data.edit_group_data = action.payload;
    },
    setGroupForDelete(state, action: PayloadAction<any>) {
      state.local_states.delete_group_data = action.payload;
    },
    setUsers(state, action: PayloadAction<any>) {
      state.data.users = action.payload;
    },
    setGroups(state, action: PayloadAction<any>) {
      state.data.groups = action.payload;
    },
    updateGroups(state, action: PayloadAction<any>) {
      state.data.groups = [action.payload, ...state.data.groups];
    },
    resetGroupData(state) {
      state.data.create_group_data = {
        uuid: '',
        name: '',
        members: [],
        approval_step_users_rule: 1,
        has_been_saved: false
      };
    },
    setLocalStates(state, action: PayloadAction<{ key: string; value: any }>) {
      state.local_states[action.payload.key] = action.payload.value;
    },
    loadApprovalData(state, action: PayloadAction<any>) {
      const currentUsers = state.data.users;
      const currentGroups = state.data.groups;
      const approvalData = normalizeApprovalDataPayload(action.payload);

      state.data = {
        ...state.data, // Keep existing values
        ...approvalData, // Override only present keys
        users: currentUsers,
        groups: currentGroups
      };

      // IF the approval is running, directly show the progress
      if (state?.data?.status === STATUS_ACTIVE || state?.data?.approval_process_final_decision_timestamp) {
        state.local_states.right_column_options = RIGHT_COLUMN_OPTIONS.RIGHT_COLUMN_PROGRESS;
      }else{
        state.local_states.right_column_options = RIGHT_COLUMN_OPTIONS.RIGHT_COLUMN_USERS;
      }

      // Initialize the right user/group selectors based on the data
      if (state?.data?.approval_step_1_group) {
        state.data.select_type = TableSelectOption.GROUP;
      }
    },
    loadApprovalFeatures(state, action: PayloadAction<any>) {
        state.featuresEnabled = {
            approvalProcessMultistep: action.payload?.approvalProcessMultistep,
            approvalProcessSaveAsApprovedPost: action.payload?.approvalProcessSaveAsApprovedPost,
          approvalProcessGroups: action.payload?.approvalProcessGroups,
        };
    },
    resetApprovalData(state) {
        state.data = {
            ...LomavisPostApprovalStateInitialState.data,
            cloud_uuid: state.data.cloud_uuid,
            location_uuid: state.data.location_uuid,
            is_multipost: state.data.is_multipost,
            users: state.data.users,
            groups: state.data.groups,
        };
        state.local_states.local_changes = false;
        state.default_settings.applied = false;
    }
  }
});

export const {
  setLoading,
  setError,
  setApprovalUI,
  setDefaultSettingsLoading,
  setDefaultSettingsError,
  loadDefaultApprovalSettings,
  setDefaultApprovalSettingsValue,
  markDefaultApprovalSettingsApplied,
  setApprovalConfiguration,
  setApprovalKey,
  setGroupValue,
  setEditGroupValue,
  setGroupForEdit,
  setGroupForDelete,
  setUsers,
  setGroups,
  updateGroups,
  setLocalStates,
  loadApprovalData,
  loadApprovalFeatures,
  resetApprovalData
} = lomavisPostApprovalSlice.actions;
export default lomavisPostApprovalSlice.reducer;

export const fetchUsers =
  (data: {
    locationUuid?: string;
    cloudUuid?: string;
    is_multipost?: boolean;
  }) =>
  async (dispatch: any) => {
    dispatch(setLoading(true));
    const payload: Record<string, any> = {};
    if (data.is_multipost) payload.is_multipost = data.is_multipost;
    if (data.cloudUuid) payload.cloud__uuid = data.cloudUuid;
    if (data.locationUuid) payload.location__uuid = data.locationUuid;
    const url = resolve('approval_process_users', {}, payload);

    try {
      const users = await fetchAllPaginatedResults(url, {}) || []
      dispatch(setUsers(users));
      return users;
    } catch (error: any) {
      dispatch(setError(error));
      return [];
    } finally {
      dispatch(setLoading(false));
    }
  };

export const fetchGroups =
  (data: {
    locationUuid?: string;
    cloudUuid?: string;
    is_multipost?: boolean;
  }) =>
  async (dispatch: any) => {
    dispatch(setLoading(true));
    const payload: Record<string, any> = {};
    if (data.is_multipost) payload.is_multipost = data.is_multipost;
    if (data.cloudUuid) payload.cloud__uuid = data.cloudUuid;
    if (data.locationUuid) payload.location__uuid = data.locationUuid;
    const url = resolve('approval_process_groups', {}, payload);
    try {
      const groups = await fetchAllPaginatedResults(url, {}) || []
      dispatch(setGroups(groups));
      return groups;
    } catch (error: any) {
      dispatch(setError(error));
      return [];
    } finally {
      dispatch(setLoading(false));
    }
  };

const resolveByUuid = (items: any[] = [], item: any) => {
  if (!item?.uuid) return item;
  return items.find((candidate: any) => candidate.uuid === item.uuid) || item;
};

const hasSelectedApprovalFields = (approvalData: any): boolean => {
  return Boolean(
    approvalData?.approval_step_1_users?.length > 0 ||
      approvalData?.approval_step_2_users?.length > 0 ||
      approvalData?.approval_step_1_group ||
      approvalData?.approval_step_2_group
  );
};

const hasDefaultApprovalSettings = (
  defaultSettings: ApprovalDefaultSettingsData | null
): boolean => {
  return Boolean(
    defaultSettings?.default_approval_user ||
      defaultSettings?.default_approval_group
  );
};

const isDraftApprovalState = (
  approvalData: any,
  creatorApprovalProcess: any
): boolean => {
  return Boolean(
    (approvalData?.status ?? STATUS_DRAFT) === STATUS_DRAFT &&
      (creatorApprovalProcess?.status ?? STATUS_DRAFT) === STATUS_DRAFT &&
      !approvalData?.approval_process_final_decision_timestamp &&
      !creatorApprovalProcess?.approval_process_final_decision_timestamp
  );
};

/**
 * Default settings describe how a *new* post starts out. Editing an existing
 * post must never have them applied on top of the data that was loaded, and a
 * reused post carries the original's approval data, so that counts as loaded
 * data too.
 */
export const isNewPostCreation = (creatorState: any): boolean => {
  const config = creatorState?.lomavisCreatorConfig;
  return !config?.is_update && !config?.importing_post;
};

export const shouldClearDefaultAppliedApprovalSettings = ({
  approvalData,
  creatorApprovalProcess,
  localChanges,
  defaultSettingsApplied,
  defaultSettings
}: {
  approvalData: any;
  creatorApprovalProcess: any;
  localChanges: boolean;
  defaultSettingsApplied: boolean;
  defaultSettings: ApprovalDefaultSettingsData | null;
}): boolean => {
  return Boolean(
    !hasDefaultApprovalSettings(defaultSettings) &&
      defaultSettingsApplied &&
      !localChanges &&
      isDraftApprovalState(approvalData, creatorApprovalProcess)
  );
};

export const shouldApplyDefaultApprovalSettings = ({
  approvalData,
  creatorApprovalProcess,
  localChanges,
  defaultSettingsApplied,
  defaultSettings,
  replaceAppliedDefaults = false
}: {
  approvalData: any;
  creatorApprovalProcess: any;
  localChanges: boolean;
  defaultSettingsApplied: boolean;
  defaultSettings: ApprovalDefaultSettingsData | null;
  replaceAppliedDefaults?: boolean;
}): boolean => {
  if (!hasDefaultApprovalSettings(defaultSettings)) {
    return false;
  }

  if (
    replaceAppliedDefaults &&
    defaultSettingsApplied &&
    !localChanges &&
    isDraftApprovalState(approvalData, creatorApprovalProcess)
  ) {
    return true;
  }

  return Boolean(
    !creatorApprovalProcess &&
      !localChanges &&
      !defaultSettingsApplied &&
      !hasSelectedApprovalFields(approvalData)
  );
};

export const buildApprovalDataWithDefaultSettings = (
  approvalData: any,
  defaultSettings: ApprovalDefaultSettingsData | null
) => {
  if (!defaultSettings?.default_approval_user && !defaultSettings?.default_approval_group) {
    return null;
  }

  const defaultApprovalUser = resolveByUuid(
    approvalData?.users,
    defaultSettings.default_approval_user
  );
  const defaultApprovalGroup = resolveByUuid(
    approvalData?.groups,
    defaultSettings.default_approval_group
  );
  const {
    users: _users,
    groups: _groups,
    ...approvalDataWithoutOptions
  } = approvalData || {};

  const nextApprovalData = {
    ...approvalDataWithoutOptions,
    step_mode: STEP_MODE_SINGLE_STEP,
    status: STATUS_DRAFT,
    current_step: 1,
    approval_step_2_users: [],
    approval_step_2_group: null,
    approval_step_2_users_rule: APPROVAL_USERS_RULE_ANY
  };

  if (defaultApprovalUser) {
    return {
      ...nextApprovalData,
      select_type: TableSelectOption.USER,
      approval_step_1_mode: APPROVAL_STEP_MODE_USER,
      approval_step_1_users: [defaultApprovalUser],
      approval_step_1_group: null,
      approval_step_1_users_rule: APPROVAL_USERS_RULE_ANY
    };
  }

  return {
    ...nextApprovalData,
    select_type: TableSelectOption.GROUP,
    approval_step_1_mode: APPROVAL_STEP_MODE_GROUP,
    approval_step_1_users: [],
    approval_step_1_group: defaultApprovalGroup,
    approval_step_1_users_rule:
      defaultApprovalGroup?.approval_step_users_rule || APPROVAL_USERS_RULE_ANY
  };
};

export const applyDefaultApprovalSettingsIfEmpty =
  (replaceAppliedDefaults = false) => async (dispatch: any, getState: any) => {
    const state = getState();
    const approvalState = state.lomavisPostApproval;
    const creatorState = state.lomavisCreator;

    // Only a post that is actually being created may take over the defaults.
    if (!isNewPostCreation(creatorState)) {
      return false;
    }

    if (
      replaceAppliedDefaults &&
      shouldClearDefaultAppliedApprovalSettings({
        approvalData: approvalState.data,
        creatorApprovalProcess: creatorState.approval_process,
        localChanges: approvalState.local_states.local_changes,
        defaultSettingsApplied: approvalState.default_settings.applied,
        defaultSettings: approvalState.default_settings.data
      })
    ) {
      dispatch(resetApprovalData());
      dispatch(updatePostApproval(null));
      return true;
    }

    if (
      !shouldApplyDefaultApprovalSettings({
        approvalData: approvalState.data,
        creatorApprovalProcess: creatorState.approval_process,
        localChanges: approvalState.local_states.local_changes,
        defaultSettingsApplied: approvalState.default_settings.applied,
        defaultSettings: approvalState.default_settings.data,
        replaceAppliedDefaults
      })
    ) {
      return false;
    }

    const approvalData = buildApprovalDataWithDefaultSettings(
      approvalState.data,
      approvalState.default_settings.data
    );

    if (!approvalData) {
      return false;
    }

    dispatch(loadApprovalData(approvalData));
    dispatch(updatePostApproval(approvalData));
    dispatch(markDefaultApprovalSettingsApplied(true));
    return true;
  };

export const fetchDefaultApprovalSettings =
  (locationUuid: string, applyDefaults = true) => async (dispatch: any) => {
    dispatch(setDefaultSettingsLoading(true));
    const url = resolve(
      'creator_default_settings',
      { locationUuid },
      {}
    );

    try {
      const response = await axiosClient.get(url);
      dispatch(loadDefaultApprovalSettings(response?.data));
      if (applyDefaults) {
        dispatch(applyDefaultApprovalSettingsIfEmpty() as any);
      }
      return response?.data;
    } catch (error: any) {
      dispatch(setDefaultSettingsError(error));
      throw error;
    } finally {
      dispatch(setDefaultSettingsLoading(false));
    }
  };

export const saveDefaultApprovalSettings =
  (locationUuid: string) => async (dispatch: any, getState: any) => {
    dispatch(setDefaultSettingsLoading(true));
    const state = getState();
    const localData = state.lomavisPostApproval.default_settings.local_data;
    const url = resolve(
      'creator_default_settings',
      { locationUuid },
      {}
    );

    const payload = {
      default_approval_user: localData.default_approval_user?.uuid || null,
      default_approval_group: localData.default_approval_group?.uuid || null
    };

    try {
      const response = await axiosClient.put(url, payload);
      dispatch(loadDefaultApprovalSettings(response?.data));
      if (isNewPostCreation(state.lomavisCreator)) {
        dispatch(applyDefaultApprovalSettingsIfEmpty(true) as any);
      }
      return response?.data;
    } catch (error: any) {
      dispatch(setDefaultSettingsError(error));
      throw error;
    } finally {
      dispatch(setDefaultSettingsLoading(false));
    }
  };

export const createGroup = () => async (dispatch: any, getState: any) => {
  dispatch(setLoading(true));
  const url = resolve('approval_process_groups', {}, {});
  const state = getState();
  const groupData = {
    ...state.lomavisPostApproval.data.create_group_data
  };
  groupData.members = groupData.members.map((member: any) => member.uuid);
  groupData.cloud = state.lomavisPostApproval.data.cloud_uuid;
  groupData.location = state.lomavisPostApproval.data.location_uuid;
  groupData.is_multipost = state.lomavisPostApproval.data.is_multipost;

  try {
    const response = await axiosClient.post(url, groupData);
    dispatch(updateGroups(response?.data));
  } catch (error: any) {
    dispatch(setError(error));
  } finally {
    dispatch(setLoading(false));
  }
};
export const editGroup = () => async (dispatch: any, getState: any) => {
  dispatch(setLoading(true));
  const state = getState();
  const groupData = {
    ...state.lomavisPostApproval.data.edit_group_data
  };
  groupData.members = groupData.members.map((member: any) => member.uuid);
  groupData.cloud = state.lomavisPostApproval.data.cloud_uuid;
  groupData.location = state.lomavisPostApproval.data.location_uuid;
  groupData.is_multipost = state.lomavisPostApproval.data.is_multipost;

  const payload: Record<string, any> = {};
  if (groupData.is_multipost) payload.is_multipost = groupData.is_multipost;
  if (groupData.cloud) payload.cloud__uuid = groupData.cloud;
  if (groupData.location) payload.location__uuid = groupData.location;

  try {
    const url = resolve(
      'approval_process_groups_edit',
      { uuid: groupData.uuid },
      payload
    );
    const response = await axiosClient.put(url, groupData);

    dispatch(
      fetchGroups({
        locationUuid: groupData.location,
        cloudUuid: groupData.cloud,
        is_multipost: groupData.is_multipost
      })
    );
  } catch (error: any) {
    dispatch(setError(error));
  } finally {
    dispatch(setLoading(false));
  }
};

export const deleteGroup = () => async (dispatch: any, getState: any) => {
  dispatch(setLoading(true));
  const state = getState();
  const groupData = {
    ...state.lomavisPostApproval.local_states.delete_group_data
  };
  groupData.members = groupData.members.map((member: any) => member.uuid);
  groupData.cloud = state.lomavisPostApproval.data.cloud_uuid;
  groupData.location = state.lomavisPostApproval.data.location_uuid;
  groupData.is_multipost = state.lomavisPostApproval.data.is_multipost;

  const payload: Record<string, any> = {};
  if (groupData.is_multipost) payload.is_multipost = groupData.is_multipost;
  if (groupData.cloud) payload.cloud__uuid = groupData.cloud;
  if (groupData.location) payload.location__uuid = groupData.location;

  try {
    const url = resolve(
      'approval_process_groups_edit',
      { uuid: groupData.uuid },
      payload
    );
    const response = await axiosClient.delete(url, groupData);

    dispatch(
      fetchGroups({
        locationUuid: groupData.location,
        cloudUuid: groupData.cloud,
        is_multipost: groupData.is_multipost
      })
    );
  } catch (error: any) {
    dispatch(setError(error));
  } finally {
    dispatch(setLoading(false));
  }
};

export const resetGroupData = () => async (dispatch: any) => {
  dispatch(setLoading(true));
  dispatch(lomavisPostApprovalSlice.actions.resetGroupData());
  dispatch(setLoading(false));
};
